/**
 * Click-to-talk — browser SpeechRecognition → text + speech of
 * Sales Brain answers only (plus fixed host bridge / read-them lines).
 *
 * Guardrail: STT in, SB text out. No freeform voice model.
 * Flow: bridge → listen → answer speech → soft continue → listen.
 *
 * Speech: SERVER-authoritative. Agent answers + host lines play a server-minted
 * mp3 reservation (speakAgentVoiceUrl / speakNavVoiceUrl); the client no longer
 * POSTs text to a server TTS endpoint. speakAgentText is the browser
 * speechSynthesis fallback for the rare case no server mp3 is available.
 */

export type VoiceTalkState = "idle" | "listening" | "unsupported" | "error";

export type VoiceTalkHandle = {
  supported: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((ev: SpeechRecognitionEventLike) => void) | null;
  onerror: ((ev: { error?: string }) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<
    ArrayLike<{ transcript: string; confidence: number }> & { isFinal?: boolean }
  >;
};

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  // Single intersection cast (not `as unknown as`) — vendor prefixes are not on lib.dom.
  const w = window as Window & {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isVoiceTalkSupported(): boolean {
  return getSpeechRecognitionCtor() != null;
}

/**
 * Create a click-to-talk recognizer.
 * Uses continuous capture + silence window so short phrases ("setup details")
 * aren't cut off by Chrome's one-shot mode.
 */
export function createVoiceTalk(opts: {
  onState: (s: VoiceTalkState) => void;
  onFinal: (transcript: string) => void;
  onInterim?: (transcript: string) => void;
  lang?: string;
  /** ms of silence after last speech before we commit (default 1600). */
  silenceMs?: number;
}): VoiceTalkHandle {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) {
    opts.onState("unsupported");
    return {
      supported: false,
      start: () => opts.onState("unsupported"),
      stop: () => {},
      abort: () => {},
    };
  }

  const silenceMs = opts.silenceMs ?? 1600;
  let rec: SpeechRecognitionLike | null = null;
  let active = false;
  let finalized = false;
  let accumulated = "";
  /** Latest bar text (finals + interim). Short chip answers often never
   *  finalize — Chrome leaves them as interim only; we must still commit. */
  let lastHeard = "";
  let silenceTimer: ReturnType<typeof setTimeout> | null = null;

  const clearSilence = () => {
    if (silenceTimer) {
      clearTimeout(silenceTimer);
      silenceTimer = null;
    }
  };

  const cleanup = () => {
    active = false;
    clearSilence();
    if (rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      try {
        rec.abort();
      } catch {
        /* ignore */
      }
    }
    rec = null;
  };

  const commit = (text: string) => {
    if (finalized) return;
    const t = text.replace(/\s+/g, " ").trim();
    if (!t) return;
    finalized = true;
    active = false;
    clearSilence();
    opts.onState("idle");
    opts.onFinal(t);
    try {
      rec?.stop();
    } catch {
      /* ignore */
    }
  };

  /** Prefer finalized text; fall back to what the visitor watched type in the bar. */
  const textToCommit = (): string =>
    (accumulated.trim() || lastHeard.trim()).replace(/\s+/g, " ").trim();

  const scheduleCommit = () => {
    clearSilence();
    silenceTimer = setTimeout(() => {
      if (!active || finalized) return;
      const t = textToCommit();
      if (t) {
        // Interim-only "Both" / "Yes" must become a turn (QA: draft in bar, never sent).
        commit(t);
      } else {
        // Silence with nothing heard
        active = false;
        opts.onState("idle");
        opts.onInterim?.("");
      }
    }, silenceMs);
  };

  return {
    supported: true,
    start() {
      if (active) return;
      cleanup();
      finalized = false;
      accumulated = "";
      lastHeard = "";
      rec = new Ctor();
      // Force en-US for product site copy unless caller overrides.
      rec.lang = opts.lang ?? "en-US";
      // Continuous + silence window: short phrases + multi-word asks.
      rec.continuous = true;
      rec.interimResults = true;
      rec.maxAlternatives = 3;
      active = true;
      opts.onState("listening");

      rec.onresult = (ev) => {
        if (!active || finalized) return;
        let interim = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const row = ev.results[i]!;
          // Best alternative by confidence when available.
          let best = row[0]?.transcript ?? "";
          let bestC = row[0]?.confidence ?? 0;
          for (let a = 1; a < row.length; a++) {
            const c = row[a]?.confidence ?? 0;
            if (c > bestC) {
              bestC = c;
              best = row[a]?.transcript ?? best;
            }
          }
          if ((row as { isFinal?: boolean }).isFinal) {
            // Low-confidence SHORT finals are ambient-noise hallucinations
            // ("sites to detect the site" — QA 2026-07-22) — drop them rather
            // than let them become turns. Real sentences rarely score this
            // low; confidence 0 means "unknown engine" and is never dropped.
            // EXCEPTION: FM chip answers ("Both", "Paid", "Yes") are often
            // short + low-confidence; dropping them left the bar filled and
            // never sent a visitor turn.
            const wordCount = best.trim().split(/\s+/).filter(Boolean).length;
            const chipAnswer = isChipStyleVoiceAnswer(best);
            if (
              !chipAnswer &&
              bestC > 0 &&
              bestC < 0.45 &&
              wordCount <= 6
            ) {
              try {
                console.warn("[BlueHat voice] dropped low-confidence final", {
                  confidence: Number(bestC.toFixed(2)),
                  preview: best.slice(0, 60),
                });
              } catch {
                /* ignore */
              }
              continue;
            }
            accumulated = `${accumulated} ${best}`.replace(/\s+/g, " ").trim();
          } else {
            interim += best;
          }
        }
        const display = [accumulated, interim].filter(Boolean).join(" ").trim();
        if (display) {
          lastHeard = display;
          if (opts.onInterim) opts.onInterim(display);
        }
        // Any speech resets silence timer; commit after pause.
        if (accumulated || interim) scheduleCommit();
      };

      rec.onerror = (ev) => {
        const err = ev.error ?? "error";
        // no-speech: keep listening a bit via onend restart, or idle.
        if (err === "aborted") {
          active = false;
          clearSilence();
          opts.onState("idle");
          return;
        }
        if (err === "no-speech") {
          // Don't hard-fail — Chrome fires this often; wait for silence timer / end.
          return;
        }
        if (err === "network") {
          // Online STT engines need network — surface soft error.
          opts.onState("error");
          active = false;
          clearSilence();
          return;
        }
        opts.onState("error");
        active = false;
        clearSilence();
      };

      rec.onend = () => {
        if (finalized || !active) return;
        // Chrome stops continuous sessions frequently — restart while active
        // so we don't drop the second half of a phrase.
        if (textToCommit()) {
          // We have something; let silence timer finish commit.
          scheduleCommit();
          try {
            rec?.start();
          } catch {
            /* already started or dead */
          }
          return;
        }
        try {
          rec?.start();
        } catch {
          active = false;
          opts.onState("idle");
        }
      };

      try {
        rec.start();
      } catch {
        opts.onState("error");
        active = false;
      }
    },
    stop() {
      clearSilence();
      const t = textToCommit();
      if (t && !finalized) {
        commit(t);
        return;
      }
      try {
        rec?.stop();
      } catch {
        /* ignore */
      }
      active = false;
      opts.onState("idle");
    },
    abort() {
      finalized = true;
      cleanup();
      opts.onState("idle");
    },
  };
}

const MUTE_KEY = "bhai_voice_whisper_muted";

export function isWhisperMuted(): boolean {
  try {
    return sessionStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setWhisperMuted(muted: boolean): void {
  try {
    if (muted) sessionStorage.setItem(MUTE_KEY, "1");
    else sessionStorage.removeItem(MUTE_KEY);
  } catch {
    /* ignore */
  }
}

export function isStopTalkingCommand(text: string): boolean {
  const t = text.replace(/\s+/g, " ").trim().toLowerCase().replace(/[?.!]+$/g, "").trim();
  if (!t) return false;
  // Exact short commands
  if (
    /^(stop talking|be quiet|quiet|mute|shut up|stop|go away|leave me alone)$/.test(
      t,
    )
  ) {
    return true;
  }
  // Prefix still counts: "stop talking please"
  if (
    /^(stop talking|be quiet|quiet|mute|shut up|go away|leave me alone)\b/.test(t)
  ) {
    return true;
  }
  // STT noise before the intent: "different meaning can you stop talking"
  // Require imperative/request form so product questions stay in chat.
  if (
    t.length <= 72 &&
    /\b((can you |could you |please )?(stop talking|be quiet|shut up)|leave me alone)\b/.test(
      t,
    ) &&
    !/\b(does|do|will|would|when|how|why|if)\b/.test(t)
  ) {
    return true;
  }
  // Bare "stop" only when the whole utterance is just that.
  return t === "stop" || t === "mute";
}

/**
 * High-precision STT flag: whisper transcripts come from OUR endpointer's
 * deliberate commits (VAD + junk gate + gain normalization), so short
 * conversational statements ("let's try that again") are REAL turns — the
 * ambient-chatter heuristics below exist for SpeechRecognition, which
 * transcribed room noise indiscriminately. The adapter sets this per engine.
 */
let highPrecisionStt = false;
export function setHighPrecisionStt(v: boolean): void {
  highPrecisionStt = v;
}

/**
 * Short enumerable chip-style replies visitors speak after FM / poles
 * ("Both", "Paid", "Yes"). Protects them from low-confidence drops and
 * interim-only silence commits that leave the bar filled but never send.
 */
export function isChipStyleVoiceAnswer(text: string): boolean {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t) return false;
  // FM poles + short enumerable chips visitors speak after SB forks.
  return /^(yes|yeah|yep|yup|no|nope|not yet|ok|okay|sure|both|paid|organic|agency|maybe|not sure|not sure yet|sounds good|let'?s do it|go ahead|continue|next)(?:\s*[.!]*)?$/i.test(
    t,
  );
}

/**
 * After host speech (cold monologue / bridge / SB answer / chips): longer
 * listen window so whisper can acquire mic/model and still catch a real ask
 * ("how it works") or short FM chip. Hard timer starts only once the engine
 * reports "listening" (see bubble startListeningOnly).
 */
export const POST_ANSWER_LISTEN_MS = 10_000;
/** Faster endpointer silence for short spoken answers after host invites. */
export const POST_ANSWER_SILENCE_MS = 1_100;

/**
 * Reject STT junk before it becomes a chat turn (voice layer only).
 * Keeps short chip-like answers (yes / paid / both) and real product asks.
 * Blocks ambient noise like "don't connect to 7.5 now" that poisons SB state.
 * In high-precision mode only the HARD filters run (empty/digits/dictation);
 * the ambient heuristics are SR-only.
 */
export function isLikelyVoiceGarbageTranscript(text: string): boolean {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t) return true;

  const lower = t.toLowerCase();
  const words = lower.split(/\s+/).filter(Boolean);

  // Valid short answer-chip style replies (spoken).
  if (isChipStyleVoiceAnswer(t)) {
    return false;
  }
  // Budget-ish short replies
  if (/^(\$?\d+k?(?:\s*[-–to]+\s*\$?\d+k?)?|\d+\s*[-–]\s*\d+k?|under\s*\$?\d+k?|\$?\d+\+)(?:\s*[.!]*)?$/i.test(t)) {
    return false;
  }

  if (t.length < 4) return true;
  if (words.length === 1 && words[0]!.length < 5) return true;

  // Mostly digits / punctuation (version strings, "7.5", etc.)
  const letters = (t.match(/[a-zA-Z]/g) ?? []).length;
  if (letters === 0) return true;
  if (letters / t.length < 0.4) return true;

  // Dictation of punctuation / nonsense phrases Chrome sometimes emits
  if (/\b(comma|period|full stop|ellipsis|apostrophe)\b/i.test(t) && words.length <= 10) {
    return true;
  }

  // Whisper path: deliberate speech — hard filters above are enough.
  if (highPrecisionStt) return false;

  const looksLikeQuestion =
    /\?\s*$/.test(t) ||
    /^(how|what|why|when|where|who|which|can|could|do|does|did|is|are|will|would|should|tell|show|explain|help)\b/i.test(
      t,
    );
  const productish =
    /\b(bluehat|blue\s*hat|seo|ads|google|demo|price|pricing|cost|traffic|convert|conversion|marketing|setup|site|website|ai|intelligence|visitor|path|path[s]?|analytics|dashboard|lead|sales|roi|social|organic|paid)\b/i.test(
      t,
    );

  // Short ambient / offline chatter without product intent
  if (!looksLikeQuestion && !productish && words.length <= 8) {
    if (
      /\b(connect|guys|parents|everyone|um+|uh+|hmm+|ah+|like|basically|actually)\b/i.test(
        lower,
      )
    ) {
      return true;
    }
    // "don't X to Y now" style without product terms
    if (/\b(don't|dont|do not)\b/i.test(lower) && /\b(now|later|wait)\b/i.test(lower)) {
      return true;
    }
    if (words.length <= 5 && !looksLikeQuestion) return true;
  }

  // Number-heavy short phrase without product terms ("connect to 7.5 now")
  if (!productish && !looksLikeQuestion && /\d/.test(t) && words.length <= 7) {
    return true;
  }

  return false;
}

/**
 * True when a sentence is a generic forward-motion closer (offer / choice / next ask).
 * Pattern-based only — no product- or page-specific hardcoding.
 */
export function isForwardMotionCloser(sentence: string): boolean {
  const t = sentence.replace(/\s+/g, " ").trim();
  if (!t || t.length < 8) return false;
  // Questions are almost always the FM beat in SB answers.
  if (/\?\s*$/.test(t)) return true;
  // Soft offers / choices that sometimes omit "?"
  if (
    /^(want|would|shall|should|can|could|ready|interested)\b/i.test(t) &&
    /\b(me|you|we|to|rather|like|help)\b/i.test(t)
  ) {
    return true;
  }
  if (
    /\b(want me to|would you rather|shall we|should we|which would you|or would you)\b/i.test(
      t,
    )
  ) {
    return true;
  }
  return false;
}

/**
 * Spoken reply from Sales Brain text only (extractive, not a freestyle LLM).
 *
 * Faithful summary rules:
 *  - Use only complete sentences from the SB answer (never mid-phrase cuts).
 *  - Keep 1–3 value sentences that fit a speech budget.
 *  - Always keep a trailing forward-motion closer when present.
 *  - Drop middle sentences if over budget — never invent claims.
 *
 * Full answer still appears on screen; voice is a complete spoken takeaway
 * so it still works if the panel is closed.
 */
export function textForSpeech(raw: string): string {
  let s = raw.replace(/\r\n/g, "\n").trim();
  if (!s) return "";
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  s = s.replace(/[*_`~#]+/g, "");
  s = s.replace(/^\s*[-*•]\s+/gm, "");
  s = s.replace(/\n{2,}/g, ". ");
  s = s.replace(/\n/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  s = s.replace(/\b(e\.g\.|i\.e\.)\b/gi, "for example");
  // Do NOT rewrite every "AI" → "A.I." — monologue lines + product names
  // were stacking as "A.I. … A.I. …" and sounded broken. Leave as "AI".
  // Trademark noise for speech
  s = s.replace(/[™®©]/g, "");

  const parts = s
    .split(/(?<=[.!?])\s+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!;

  const last = parts[parts.length - 1]!;
  const hasCloser = isForwardMotionCloser(last);
  const body = hasCloser ? parts.slice(0, -1) : parts;
  const closer = hasCloser ? last : null;

  // Speech budget: enough for a real answer, not an audiobook.
  // First-sentence-first TTS still starts audio on chunk 0 only.
  const MAX_CHARS = 420;
  const MAX_BODY = 3;

  const chosen: string[] = [];
  for (let i = 0; i < body.length && chosen.length < MAX_BODY; i++) {
    const next = body[i]!;
    const trial = [...chosen, next, ...(closer ? [closer] : [])].join(" ");
    if (chosen.length === 0) {
      // Always keep the first complete sentence (never slice mid-phrase).
      chosen.push(next);
      continue;
    }
    if (trial.length <= MAX_CHARS) {
      chosen.push(next);
    } else {
      break;
    }
  }

  if (closer) {
    const withCloser = [...chosen, closer].join(" ");
    if (withCloser.length <= MAX_CHARS + 60 || chosen.length <= 1) {
      // Prefer keeping the closer; drop trailing body sentences if needed.
      while (chosen.length > 1 && [...chosen, closer].join(" ").length > MAX_CHARS + 60) {
        chosen.pop();
      }
      chosen.push(closer);
    } else {
      chosen.push(closer);
    }
  }

  return chosen.join(" ").replace(/\s+/g, " ").trim();
}

/** Sentence chunks for pipelined OpenAI TTS (first chunk plays ASAP). */
export function splitSpeechChunks(text: string): string[] {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t) return [];
  const parts = t.split(/(?<=[.!?])\s+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return [t];
  // Keep the first chunk short so OpenAI returns first audio faster (~1 sentence).
  const out: string[] = [];
  if (parts[0]) {
    out.push(parts[0]!);
  }
  // Merge the rest into ~160 char chunks.
  let buf = "";
  for (const p of parts.slice(1)) {
    const next = buf ? `${buf} ${p}` : p;
    if (next.length <= 160 || !buf) {
      buf = next;
    } else {
      out.push(buf);
      buf = p;
    }
    if (buf.length >= 140) {
      out.push(buf);
      buf = "";
    }
  }
  if (buf) out.push(buf);
  return out;
}

let preferredVoice: SpeechSynthesisVoice | null = null;
// Keep the retired browser-TTS fallback compiled (policy: never wrong-voice).
void ((): unknown => speakBrowserTts);

/** Score voices — higher = more natural-sounding for English product speech. */
export function scoreVoice(v: SpeechSynthesisVoice): number {
  let score = 0;
  const n = v.name;
  const lang = v.lang || "";
  if (!/^en/i.test(lang)) return -100;
  if (/en-US/i.test(lang)) score += 8;
  else if (/en-GB|en-AU|en-CA/i.test(lang)) score += 5;
  else score += 2;

  // Strongly prefer neural / premium / natural labels.
  if (/neural|natural|premium|enhanced|superstar|wavenet|studio/i.test(n)) score += 40;
  // Known better system voices
  if (/samantha|karen|moira|fiona|tessa|veena|rishi/i.test(n)) score += 30;
  if (/google us english/i.test(n)) score += 35;
  if (/google uk english female/i.test(n)) score += 32;
  if (/microsoft (aria|jenny|guy|davis|tony|sara)/i.test(n)) score += 34;
  // Penalize compact / novelty / clearly synthetic defaults
  if (/compact|eloquence|zarvox|trinoids|bad news|good news|whisper|organ|cellos/i.test(n))
    score -= 50;
  if (/alex|fred|daniel|thomas/i.test(n) && !/neural|natural/i.test(n)) score -= 5;
  // Prefer local high-quality on macOS when marked
  if (v.localService && /samantha|enhanced|premium/i.test(n)) score += 10;
  return score;
}

function pickNaturalVoice(): SpeechSynthesisVoice | null {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return preferredVoice;
  let best: SpeechSynthesisVoice | null = null;
  let bestScore = -Infinity;
  for (const v of voices) {
    const s = scoreVoice(v);
    if (s > bestScore) {
      bestScore = s;
      best = v;
    }
  }
  preferredVoice = best;
  return preferredVoice;
}

export function warmSpeechVoices(): void {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  pickNaturalVoice();
  window.speechSynthesis.onvoiceschanged = () => {
    pickNaturalVoice();
  };
}

/** Widget-api origin — used to resolve SERVER voice mp3 URLs (fullVoiceUrl). */
let ttsApiOrigin: string | null = null;
/**
 * Single long-lived Audio element for ALL host/answer TTS.
 * Mobile Safari (and many Android browsers) only allow play() after a user
 * gesture on the SAME element that was primed — creating `new Audio()` after
 * an async SB wait / mp3 fetch is silently blocked ("voice won't speak on
 * mobile"). Unlock on AI Voice tap, reuse forever.
 */
let currentAudio: HTMLAudioElement | null = null;
let currentObjectUrl: string | null = null;
/** True after a successful unlock play during a user gesture. */
let audioUnlocked = false;
/** Bumped on stop / new speak so a late onDone never steals the mic. */
let speakGeneration = 0;
/** True while a speak pipeline is active (async fetch + audio). Scroll must not cut this. */
let agentSpeechBusy = false;
/**
 * Who owns the current audio:
 * - session = SB answer / turn follow-up — survives SPA nav + panel close
 * - nav = cold_open / engaged_nav monologue — ALWAYS interruptible on route change
 *
 * Restores interruptible nav TTS (bf7c7e7e) which never merged to main —
 * without this, isAgentSpeechActive() treated nav monologues like answers
 * and SPA nav refused to cut them ("voice kept going").
 */
export type AgentSpeechRole = "session" | "nav";
let agentSpeechRole: AgentSpeechRole | null = null;
/** Optional callback when speech fully ends (natural complete or stop). */
let onAgentSpeechIdle: (() => void) | null = null;

/** True while OpenAI/browser agent speech is in flight — never start a new host line mid-sentence. */
export function isAgentSpeechActive(): boolean {
  if (agentSpeechBusy) return true;
  if (currentAudio && !currentAudio.paused && !currentAudio.ended) return true;
  try {
    if (typeof window !== "undefined" && window.speechSynthesis?.speaking) {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/** Role of the currently playing line (null when idle). */
export function getAgentSpeechRole(): AgentSpeechRole | null {
  return isAgentSpeechActive() ? agentSpeechRole : null;
}

/**
 * True only for in-flight SESSION voice (SB answer / turn continuity).
 * Nav monologues return false so SPA navigation can cut them and play the new page.
 */
export function isSessionSpeechActive(): boolean {
  return isAgentSpeechActive() && agentSpeechRole === "session";
}

/** Fire once when speech goes idle (for deferred section announces). */
export function setOnAgentSpeechIdle(cb: (() => void) | null): void {
  onAgentSpeechIdle = cb;
}

function markAgentSpeechIdle(): void {
  agentSpeechBusy = false;
  agentSpeechRole = null;
  const cb = onAgentSpeechIdle;
  if (cb) {
    try {
      cb();
    } catch {
      /* ignore */
    }
  }
}
/** No-op retained for callers: the client no longer makes any OpenAI TTS /
 *  voice-summary HTTP calls (those endpoints are retired). In-flight SERVER mp3
 *  audio is stopped via clearOpenAiAudio() in stopAgentSpeech. */
export function abortOpenAiVoiceNetwork(): void {
  /* nothing to abort — no client-side voice HTTP anymore */
}

/** Call from snippet with the bh.js script origin (e.g. https://slug.w…:8080). */
export function setTtsApiOrigin(origin: string): void {
  const o = origin.replace(/\/$/, "").trim();
  ttsApiOrigin = o || null;
}

/** Widget-api origin for session-less voice/host endpoints (tts, rank, summary). */
export function getTtsApiOrigin(): string | null {
  return ttsApiOrigin;
}

/** Retained for the snippet config path. The TTS voice is now resolved
 *  SERVER-side (per tenant) when synthesizing reservation mp3s; the client no
 *  longer picks a voice for its own synth. No-op. */
export function setTtsVoice(_voice: string): void {
  /* server-authoritative voice — client no longer synthesizes */
}

/** Minimal silent wav (data URI) — enough to prime iOS media engagement. */
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

/**
 * Shared Audio element with iOS playsInline flags. Must be the same node
 * unlocked on the AI Voice tap and used for every later speak.
 */
function getSharedAudio(): HTMLAudioElement {
  if (currentAudio) return currentAudio;
  const a = new Audio();
  a.preload = "auto";
  // iOS: without playsinline, play() can fail or force fullscreen video.
  try {
    a.setAttribute("playsinline", "true");
    a.setAttribute("webkit-playsinline", "true");
    (a as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
  } catch {
    /* ignore */
  }
  currentAudio = a;
  return a;
}

/**
 * Call on AI Voice / mic tap (user gesture) so later answer playback isn't
 * blocked by mobile autoplay after the async Sales Brain wait.
 * Primes the SHARED audio element — do not create a throwaway Audio here.
 */
export function unlockAudioPlayback(): void {
  if (typeof window === "undefined") return;
  try {
    const a = getSharedAudio();
    // If already unlocked and not idle-broken, keep the element as-is.
    // Still re-prime on every gesture — iOS can revoke unlock after long idle.
    try {
      a.pause();
    } catch {
      /* ignore */
    }
    a.src = SILENT_WAV;
    a.volume = 0.01;
    a.muted = false;
    const p = a.play();
    if (p && typeof p.then === "function") {
      void p
        .then(() => {
          audioUnlocked = true;
          try {
            a.pause();
            a.currentTime = 0;
          } catch {
            /* ignore */
          }
        })
        .catch(() => {
          /* gesture lost — next real play may still fail on mobile */
          try {
            console.warn("[BlueHat voice] unlockAudioPlayback play() blocked");
          } catch {
            /* ignore */
          }
        });
    } else {
      audioUnlocked = true;
    }
  } catch {
    /* ignore */
  }
  // Also warm browser TTS voices while we're in a gesture.
  warmSpeechVoices();
}

/** No-ops retained for callers. The client no longer synthesizes via a server
 *  TTS endpoint (retired) — server mp3 reservations own voice, browser TTS is
 *  the only client fallback, so there is nothing to warm or prefetch. */
export function warmOpenAiTts(): void {
  /* server-authoritative voice — nothing to warm */
}

export function prefetchSpeakText(_raw: string): void {
  /* server-authoritative voice — nothing to prefetch */
}

/** Same text path as speakAgentText so prefetch cache keys match play. */
export function normalizeSpeakText(raw: string): string {
  const head = raw.trim().slice(0, 48).toLowerCase();
  const looksLikeHostGuide =
    head.startsWith("got it") ||
    head.startsWith("pick a question") ||
    head.startsWith("now you") ||
    head.startsWith("next") ||
    head.startsWith("sure") ||
    head.startsWith("your turn") ||
    head.startsWith("you can ask") ||
    head.startsWith("let's talk") ||
    (raw.length < 280 && /stop talking/i.test(raw));
  return looksLikeHostGuide
    ? raw.replace(/\s+/g, " ").trim()
    : textForSpeech(raw);
}

export function isHostGuideSpeech(raw: string): boolean {
  const head = raw.trim().slice(0, 48).toLowerCase();
  return (
    head.startsWith("got it") ||
    head.startsWith("okay") ||
    head.startsWith("ok.") ||
    head.startsWith("pick a question") ||
    head.startsWith("now you") ||
    head.startsWith("next") ||
    head.startsWith("sure") ||
    head.startsWith("your turn") ||
    head.startsWith("you can ask") ||
    head.startsWith("let's talk") ||
    head.startsWith("one moment") ||
    (raw.length < 280 && /stop talking/i.test(raw))
  );
}


/**
 * Stop current playback but KEEP the shared Audio element (mobile unlock).
 * Destroying the element after unlock is what made iOS go silent.
 */
function clearOpenAiAudio(): void {
  if (currentAudio) {
    try {
      currentAudio.onended = null;
      currentAudio.onerror = null;
      currentAudio.onpause = null;
      currentAudio.onstalled = null;
      currentAudio.onwaiting = null;
      currentAudio.pause();
      // Leave element alive; blank src so the next play sets a real mp3.
      currentAudio.removeAttribute("src");
      // Don't call load() aggressively — can drop iOS unlock on some versions.
    } catch {
      /* ignore */
    }
  }
  if (currentObjectUrl) {
    try {
      URL.revokeObjectURL(currentObjectUrl);
    } catch {
      /* ignore */
    }
    currentObjectUrl = null;
  }
}


/**
 * Browser fallback — sentence chunks, softer rate.
 * Used when OpenAI TTS is unavailable or fails.
 */
// Retained but intentionally unreferenced by the speech paths: the browser OS
// voice must never substitute for the tenant's configured voice.
function speakBrowserTts(text: string, onDone?: () => void): void {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    onDone?.();
    return;
  }
  try {
    window.speechSynthesis.cancel();
    warmSpeechVoices();
    const voice = pickNaturalVoice();
    const chunks = text.split(/(?<=[.!?])\s+/).filter((c) => c.trim().length > 0);
    const parts = chunks.length ? chunks : [text];
    let remaining = parts.length;
    const finish = () => {
      remaining -= 1;
      if (remaining <= 0) onDone?.();
    };

    for (const chunk of parts) {
      const u = new SpeechSynthesisUtterance(chunk.trim());
      if (voice) u.voice = voice;
      u.rate = 0.92;
      u.pitch = 1.0;
      u.volume = 1;
      u.lang = voice?.lang || "en-US";
      u.onend = finish;
      u.onerror = finish;
      window.speechSynthesis.speak(u);
    }
  } catch {
    onDone?.();
  }
}

/**
 * Speak agent / bridge text with OpenAI natural TTS when available.
 *
 * Sales Brain answers (this branch): **extractive only for the critical path** —
 * no serial voice-summary wait after text is already on screen (that was ~1–2s+
 * of dead air on top of 5–8s runtime). Faithful extract of complete sentences
 * from the SB text only — never freestyle. Host guides: fixed copy.
 * Chunked TTS so first sentence audio starts ASAP.
 */
export function speakAgentText(raw: string, onDone?: () => void): void {
  const gen = ++speakGeneration;
  const tSpeak0 =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  agentSpeechBusy = true;
  agentSpeechRole = "session";
  const done = () => {
    if (gen !== speakGeneration) return;
    markAgentSpeechIdle();
    onDone?.();
  };

  if (isWhisperMuted()) {
    done();
    return;
  }

  // Stop browser TTS only — do NOT cancel OpenAI cache/in-flight for this text.
  try {
    window.speechSynthesis?.cancel();
  } catch {
    /* ignore */
  }
  clearOpenAiAudio();

  void (async () => {
    if (gen !== speakGeneration) {
      markAgentSpeechIdle();
      return;
    }

    let text: string;
    let speechSource: string = "host";
    if (isHostGuideSpeech(raw)) {
      text = raw.replace(/\s+/g, " ").trim();
    } else {
      // Fast path: local extractive only — same SB facts, no mini hop before TTS.
      text = textForSpeech(raw);
      speechSource = "extractive_fast";
      if (!text) {
        done();
        return;
      }
      try {
        console.log("[BlueHat voice] speech source =", speechSource, {
          gapToSpeakStartMs: Math.round(
            (typeof performance !== "undefined"
              ? performance.now()
              : Date.now()) - tSpeak0,
          ),
          chars: text.length,
          preview: text.slice(0, 140),
        });
      } catch {
        /* ignore */
      }
    }

    if (!text) {
      done();
      return;
    }

    // Server-authoritative voice is the ONLY path. A missing server mp3 must
    // NEVER fall back to the browser's OS voice — the session flips between
    // the tenant's voice and a random female/male system voice (QA
    // 2026-07-22: "my god, it is switching between female and male voices").
    // The answer text is already on screen; silence is correct. done() keeps
    // the follow-up/listen chain alive.
    void speechSource;
    void tSpeak0;
    if (gen !== speakGeneration) {
      markAgentSpeechIdle();
      return;
    }
    try {
      console.warn("[BlueHat voice] no server mp3 for this line — staying silent (never wrong-voice)", {
        preview: text.slice(0, 60),
      });
    } catch { /* ignore */ }
    done();
  })();
}

/** Resolve a server voice path to an absolute URL against the widget-api origin. */
function fullVoiceUrl(url: string): string {
  if (/^https?:\/\//.test(url)) return url;
  return `${ttsApiOrigin ?? ""}${url}`;
}

/**
 * Stream a SERVER-provided mp3 URL (full download then play — immune to SPA
 * nav starving a streaming src). Returns false on any failure.
 * Uses the SHARED audio element primed by unlockAudioPlayback (mobile).
 */
async function playServerVoiceUrl(url: string, gen: number): Promise<boolean> {
  if (gen !== speakGeneration || typeof window === "undefined") return false;
  // Stop prior line but keep the unlocked element.
  clearOpenAiAudio();
  // Download the WHOLE mp3 before playing (they are small). A streaming src
  // can silently STALL mid-sentence when navigation fires its request burst
  // (QA 2026-07-22: answer went inaudible on nav, then "continued the last
  // bit" on reopen — classic buffer starvation, not a stop). Local blob
  // playback is immune to network contention.
  let src = fullVoiceUrl(url);
  let objectUrl: string | null = null;
  try {
    const res = await fetch(src);
    if (res.ok) {
      const blob = await res.blob();
      if (gen !== speakGeneration) return false;
      objectUrl = URL.createObjectURL(blob);
      src = objectUrl;
    }
  } catch {
    /* fall through — element streaming as last resort */
  }
  if (gen !== speakGeneration) {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    return false;
  }
  const audio = getSharedAudio();
  currentObjectUrl = objectUrl;
  try {
    audio.pause();
  } catch {
    /* ignore */
  }
  audio.volume = 1;
  audio.muted = false;
  audio.src = src;
  // Tripwires: silence must always name itself. A pause/stall mid-playback
  // that our code didn't order is logged with position + duration.
  const logQuiet = (what: string) => {
    try {
      if (audio.ended) return;
      console.warn(`[BlueHat voice] answer audio ${what} mid-playback`, {
        at: Number(audio.currentTime.toFixed(1)),
        of: Number.isFinite(audio.duration) ? Number(audio.duration.toFixed(1)) : -1,
        unlocked: audioUnlocked,
      });
    } catch {
      /* ignore */
    }
  };
  audio.onpause = () => logQuiet("PAUSED");
  audio.onstalled = () => logQuiet("STALLED");
  audio.onwaiting = () => logQuiet("WAITING (buffering)");
  try {
    await new Promise<void>((resolve, reject) => {
      audio.onended = () => resolve();
      audio.onerror = () => reject(new Error("voice_url_play_failed"));
      const p = audio.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          audioUnlocked = true;
        }).catch(reject);
      }
    });
  } catch (err) {
    try {
      console.warn("[BlueHat voice] play() failed (often mobile autoplay)", {
        unlocked: audioUnlocked,
        err: String(err),
        url: url.slice(0, 60),
      });
    } catch {
      /* ignore */
    }
    return false;
  }
  return gen === speakGeneration;
}

/**
 * Speak an agent answer via the SERVER's mp3 URL (server-authoritative — the
 * client no longer POSTs text to synthesize). Falls back to browser TTS of the
 * answer text on any failure (402/404/timeout).
 */
export function speakAgentVoiceUrl(url: string, fallbackRaw: string, onDone?: () => void): void {
  const gen = ++speakGeneration;
  agentSpeechBusy = true;
  agentSpeechRole = "session";
  const done = () => {
    if (gen !== speakGeneration) return;
    markAgentSpeechIdle();
    onDone?.();
  };
  if (isWhisperMuted()) {
    done();
    return;
  }
  try {
    window.speechSynthesis?.cancel();
  } catch {
    /* ignore */
  }
  // Do NOT clear+recreate Audio here — reuse the unlocked element (mobile);
  // playServerVoiceUrl's internal clearOpenAiAudio stops prior playback while
  // keeping the shared element alive.
  void (async () => {
    if (gen !== speakGeneration) {
      markAgentSpeechIdle();
      return;
    }
    // If never unlocked (e.g. answer arrived without a prior voice tap), try now
    // — may still fail on strict mobile, but desktop is fine.
    if (!audioUnlocked) unlockAudioPlayback();
    let ok = await playServerVoiceUrl(url, gen);
    // One retry: re-prime shared element then play again.
    if (!ok && gen === speakGeneration) {
      unlockAudioPlayback();
      ok = await playServerVoiceUrl(url, gen);
    }
    if (gen !== speakGeneration) {
      markAgentSpeechIdle();
      return;
    }
    if (ok) {
      done();
      return;
    }
    // Server mp3 failed (expired reservation / transient error). Same rule:
    // the tenant's voice or nothing — never the OS voice.
    try {
      console.warn("[BlueHat voice] server mp3 failed — staying silent (never wrong-voice)", {
        url: url.slice(0, 80),
      });
    } catch { /* ignore */ }
    void fallbackRaw;
    done();
  })();
}

/**
 * Play a host-line mp3 (cold monologue / engaged nav / follow_up / etc.).
 * Role defaults to "nav" (interruptible on SPA route change). Pass "session"
 * for turn continuity (follow_up / read_them) that must survive navigation.
 * No text fallback — if the server audio fails, stay silent.
 */
export function speakNavVoiceUrl(
  url: string,
  onDone?: () => void,
  role: AgentSpeechRole = "nav",
): void {
  const gen = ++speakGeneration;
  agentSpeechBusy = true;
  agentSpeechRole = role;
  const done = () => {
    if (gen !== speakGeneration) return;
    markAgentSpeechIdle();
    onDone?.();
  };
  if (isWhisperMuted()) {
    done();
    return;
  }
  try {
    window.speechSynthesis?.cancel();
  } catch {
    /* ignore */
  }
  void (async () => {
    if (gen !== speakGeneration) {
      markAgentSpeechIdle();
      return;
    }
    if (!audioUnlocked) unlockAudioPlayback();
    let ok = await playServerVoiceUrl(url, gen).catch(() => false);
    if (!ok && gen === speakGeneration) {
      unlockAudioPlayback();
      ok = await playServerVoiceUrl(url, gen).catch(() => false);
    }
    void ok;
    done();
  })();
}

export function stopAgentSpeech(): void {
  // Tripwire: cutting SESSION (SB) answer mid-playback is almost always a bug.
  // Nav monologue stops on SPA route change are expected — don't alarm on those.
  if (isSessionSpeechActive()) {
    try {
      const stack = (new Error().stack ?? "")
        .split("\n")
        .slice(2, 8)
        .map((l) => l.trim())
        .join(" <- ");
      console.warn(`[BlueHat voice] SESSION speech STOPPED mid-playback BY: ${stack}`);
    } catch {
      /* ignore */
    }
  }
  speakGeneration += 1;
  try {
    window.speechSynthesis?.cancel();
  } catch {
    /* ignore */
  }
  clearOpenAiAudio();
  // Drop any in-flight OpenAI TTS / voice-summary requests (stop talking).
  abortOpenAiVoiceNetwork();
  markAgentSpeechIdle();
}

/** Shorten a pill for spoken bridge (not for chat send). */
function spokenPill(q: string): string {
  const t = q.replace(/\s+/g, " ").trim().replace(/[?]+$/, "");
  if (t.length <= 72) return t;
  return t.slice(0, 70).replace(/\s+\S*$/, "");
}

function titleish(heading: string): string {
  const t = heading.replace(/\s+/g, " ").trim();
  if (!t) return "";
  if (t === t.toUpperCase() && t.length <= 40) {
    return t.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return t;
}

export type VoiceBridgeInput = {
  currentHeading?: string;
  currentQuestions?: readonly string[];
  nextHeading?: string | null;
  nextQuestions?: readonly string[];
};

/**
 * Legacy cold fallback — short fixed invite when no page summary material.
 * Prefer buildColdOpenBridge when monologue / sections are available.
 */
export function buildVoiceBridge(input: VoiceBridgeInput): string {
  void input;
  return (
    "Pick a question on the screen, or ask me anything. " +
    "If you need me again, just hit the AI Voice button."
  );
}

/**
 * Push-to-talk / post-host listen window (ms).
 * Tuned to 5s for chip / nav answer windows (local QA cut) — was 10s on main
 * and never re-landed when interruptible-nav work stayed on a local branch.
 */
export const PUSH_TO_TALK_LISTEN_MS = 5_000;

/** Generic / useless title phrases — never speak as "this page is about X". */
const BAD_PAGE_TOPICS = new Set([
  "home",
  "homepage",
  "my website",
  "website",
  "welcome",
  "untitled",
  "index",
  "main",
  "page",
]);

/** Layout chrome — never speak as section orientation (matches text-continuity). */
function isJunkSpokenHeading(heading: string | null | undefined): boolean {
  const t = (heading ?? "").replace(/\s+/g, " ").trim().toLowerCase();
  if (!t || t.length < 2) return true;
  if (
    /^(top of (the )?page|bottom of (the )?page|header|footer|nav|navigation|sidebar|menu|hero|banner|cta|further down|near the end|more on this page)$/i.test(
      t,
    )
  ) {
    return true;
  }
  if (
    /^(on your (web)?site|your website|my website|this website|the website|website|home|homepage)$/i.test(
      t,
    )
  ) {
    return true;
  }
  if (/^(section|content|main|page|overview|intro|introduction)$/i.test(t)) {
    return true;
  }
  return false;
}

function cleanSpokenTopic(raw: string | null | undefined): string {
  const t = (raw ?? "").replace(/\s+/g, " ").trim();
  if (!t || t.length > 48) return "";
  if (BAD_PAGE_TOPICS.has(t.toLowerCase())) return "";
  if (isJunkSpokenHeading(t)) return "";
  // Sentence-like taglines are not page names.
  if (/[.!?]$/.test(t) || t.split(" ").length > 6) return "";
  return titleish(t);
}

export type ColdOpenBridgeInput = {
  /** Optional page title (filtered — not spoken alone as the whole pitch). */
  pageTopic?: string | null;
  /** PR-B hero monologue — preferred grounded page summary. */
  heroMonologue?: readonly string[];
  /** PR-B invite lines — secondary grounded copy. */
  inviteLines?: readonly string[];
  /** Section headings for light "what this page covers" orientation. */
  sectionHeadings?: readonly string[];
  /**
   * cold = first AI Voice tap (includes re-tap cue).
   * nav = SPA while voice session is on (page intro, no re-tap cue).
   */
  variant?: "cold" | "nav";
};

/**
 * Page intro for cold AI Voice and for SPA nav while the session is still on.
 * Summary + pick/ask. Cold variant also tells them how to re-open after auto-off.
 */
export function buildColdOpenBridge(input: ColdOpenBridgeInput): string {
  const mono = (input.heroMonologue ?? [])
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length >= 12);
  const invites = (input.inviteLines ?? [])
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length >= 12);
  const headings = (input.sectionHeadings ?? [])
    .map((s) => titleish(s))
    .filter(Boolean)
    .filter((h) => !isJunkSpokenHeading(h))
    // Drop near-duplicates / noise
    .filter((h, i, arr) => arr.findIndex((x) => x.toLowerCase() === h.toLowerCase()) === i)
    .slice(0, 4);
  const topic = cleanSpokenTopic(input.pageTopic);
  const isNav = input.variant === "nav";
  const pickAsk = isNav
    ? "Pick a question on the screen, or ask me anything."
    : "Pick a question on the screen, or ask me anything. " +
      "If you need me again, just hit the AI Voice button.";

  // 1) Authored monologue = best page summary (already grounded for this site).
  if (mono.length > 0) {
    // 1–2 short lines max for TTS latency.
    const summary = mono.slice(0, 2).join(" ");
    return `${summary} ${pickAsk}`;
  }

  // 2) Invite lines (also site-authored).
  if (invites.length > 0) {
    return `${invites[0]} ${pickAsk}`;
  }

  // 3) Section orientation — what the page covers, not "On {title}:" + first pill.
  if (headings.length >= 2) {
    const listed =
      headings.length === 2
        ? `${headings[0]} and ${headings[1]}`
        : `${headings.slice(0, -1).join(", ")}, and ${headings[headings.length - 1]}`;
    const lead = topic
      ? `This page is about ${topic}. It covers ${listed}.`
      : `This page covers ${listed}.`;
    return `${lead} ${pickAsk}`;
  }

  if (headings.length === 1) {
    const lead = topic
      ? `This page is about ${topic}, including ${headings[0]}.`
      : `This page covers ${headings[0]}.`;
    return `${lead} ${pickAsk}`;
  }

  // 4) Topic alone only if clean — still no sidebar pill dump.
  if (topic) {
    return `This page is about ${topic}. ${pickAsk}`;
  }

  if (isNav) {
    return "Pick a question on the screen, or ask me anything.";
  }
  return buildVoiceBridge({});
}

/**
 * Panel open / eyes-on: visitor can see pills — short invite only, no pill dump.
 */
export function buildVoicePickOrAskInvite(): string {
  return "Pick your next question, or just ask me.";
}

/**
 * FM answer chips (budget / yes-no choices) — only these, never page pills.
 */
export function buildVoiceFmChoices(choices: readonly string[]): string {
  const qs = choices
    .map((q) => spokenPill(q))
    .filter(Boolean)
    .slice(0, 4);
  if (qs.length === 0) return buildVoicePickOrAskInvite();
  if (qs.length === 1) {
    return `You can pick: ${qs[0]}. Or ask me anything.`;
  }
  if (qs.length === 2) {
    return `You can pick: ${qs[0]}, or ${qs[1]}. Or ask me anything.`;
  }
  if (qs.length === 3) {
    return `You can pick: ${qs[0]}, ${qs[1]}, or ${qs[2]}. Or ask me anything.`;
  }
  return `You can pick: ${qs[0]}, ${qs[1]}, ${qs[2]}, or ${qs[3]}. Or ask me anything.`;
}

export type VoiceFollowUpMode = "fm" | "next" | "none";

/**
 * Host line after an answer / on re-engage.
 * - fm: only forward-motion answer chips (never mix page pills).
 * - next: dead chrome / context_next_ask / page pills summary.
 * - none: short pick/ask.
 */
export function buildVoiceFollowUpInvite(
  panelOpen: boolean,
  questions: readonly string[],
  mode: VoiceFollowUpMode = "next",
): string {
  if (mode === "fm" && questions.length > 0) {
    return buildVoiceFmChoices(questions);
  }
  if (mode === "next" && questions.length > 0) {
    return buildVoiceNextSteps(questions);
  }
  if (panelOpen) return buildVoicePickOrAskInvite();
  return buildVoiceContinueCue();
}

export type PageArrivalVoiceInput = {
  /** Page topic from title/h1 (e.g. "Conversion Intelligence"). */
  pageTopic?: string | null;
  currentHeading?: string;
  currentQuestions?: readonly string[];
  nextHeading?: string | null;
  nextQuestions?: readonly string[];
};

/**
 * Spoken guide after SPA navigation while voice session is active.
 * Legacy multi-pill form — prefer buildNavHostOffer for natural host voice.
 */
export function buildPageArrivalBridge(input: PageArrivalVoiceInput): string {
  const page = titleish(input.pageTopic ?? "") || "this page";
  const here = titleish(input.currentHeading ?? "");
  const ahead = titleish(input.nextHeading ?? "");
  const nowQs = (input.currentQuestions ?? [])
    .slice(0, 2)
    .map(spokenPill)
    .filter(Boolean);
  const nextQs = (input.nextQuestions ?? [])
    .slice(0, 2)
    .map(spokenPill)
    .filter(Boolean);

  const parts: string[] = [];
  parts.push(`Now you're viewing ${page}.`);

  if (here && nowQs.length > 0) {
    parts.push(
      `Let's talk about ${here}. This section can answer: ${nowQs.join(". Or: ")}.`,
    );
  } else if (here) {
    parts.push(`Let's talk about ${here}.`);
  } else if (nowQs.length > 0) {
    parts.push(`On screen I can answer: ${nowQs.join(". Or: ")}.`);
  } else {
    parts.push("Ask me anything about what's on this page.");
  }

  if (ahead && nextQs.length > 0) {
    parts.push(
      `Coming up, ${ahead} covers: ${nextQs.join(", or ")}.`,
    );
  } else if (nextQs.length > 0) {
    parts.push(`Coming up I can also cover: ${nextQs.join(", or ")}.`);
  }

  parts.push("Tap a question, or ask me. Say stop talking anytime.");
  return parts.join(" ");
}

/** Default listen window after a nav host offer (ms) — same as push-to-talk. */
export const NAV_HOST_LISTEN_MS = PUSH_TO_TALK_LISTEN_MS;

/**
 * Pick one grounded pill for the nav host (not a list).
 * Prefer current-section questions; first usable string wins.
 */
export function pickNavHostPill(
  currentQuestions: readonly string[] = [],
  fallbackQuestions: readonly string[] = [],
): string | null {
  const pool = [...currentQuestions, ...fallbackQuestions];
  for (const raw of pool) {
    const t = raw.replace(/\s+/g, " ").trim();
    if (t.length >= 8 && t.length <= 160) return t;
  }
  return null;
}

/**
 * Scenario 2 — engaged session continuity (presentation only).
 * Ties prior topic to this page (nav) or this section (scroll).
 * One pill + optional meta out; never freestyle product claims.
 *
 * Meta instructions (pick / ask / stop talking) are rotated by the caller:
 * say on 1st engaged nav, skip 2nd, say 3rd, … — not every page land.
 */
export type EngagedContinuityInput = {
  /** nav = SPA/page land; section = scroll settle into a matching section */
  mode: "nav" | "section";
  /** From conversation focus when inventory matches; omit if no real match */
  topicLabel?: string | null;
  pageTopic?: string | null;
  sectionHeading?: string | null;
  /**
   * Path-specific cold summary from page-voice (heroMonologue for THIS url).
   * Spoken first so SPA land still orients to the page — not pill-only.
   */
  pageSummary?: readonly string[] | null;
  /** Single ranked pill (caller ranks by focus) */
  pill?: string | null;
  /**
   * When true, append pick/ask/stop-talking meta.
   * Caller should set true on engaged nav 1, 3, 5… only.
   */
  includeMetaInstructions?: boolean;
};

/** Spoken meta for engaged browse — not every nav. */
export const ENGAGED_NAV_META_INSTRUCTIONS =
  "Pick another question on the page, ask me anything, or say stop talking.";

/** Short stop cue for path revisits (always offer a way out). */
export const REVISIT_STOP_CUE = "Say stop talking anytime.";

/**
 * 1-based engaged nav index → include meta on odd lands (1st, 3rd, 5th…).
 */
export function shouldIncludeEngagedNavMeta(engagedNavIndex: number): boolean {
  return engagedNavIndex >= 1 && engagedNavIndex % 2 === 1;
}

/**
 * Revisit land only (already heard monologue this session).
 * One logical pill + stop cue — no monologue re-read.
 */
export function buildRevisitNavOffer(pill: string | null | undefined): string {
  const p = pill ? spokenPill(pill) : "";
  if (p) {
    return `${p}? Or pick another on the screen. ${REVISIT_STOP_CUE}`;
  }
  return `Pick a question on the screen, or ask me anything. ${REVISIT_STOP_CUE}`;
}

/**
 * Build spoken continuity for engaged browse (chat may be closed).
 * Returns null when section mode has no topic match (caller stays quiet).
 *
 * Nav speech: path-specific page summary (from page-voice) + optional
 * continuity topic + one ranked pill. Never "This page is …" / layout chrome.
 * Pill-only sounded thin (telemetry: single short question on every land).
 */
export function buildEngagedContinuityOffer(
  input: EngagedContinuityInput,
): string | null {
  const topic = (input.topicLabel ?? "").replace(/\s+/g, " ").trim();
  const pill = input.pill ? spokenPill(input.pill) : "";
  // pageTopic / sectionHeading unused for speech framing — monologue carries page.
  void input.pageTopic;
  void input.sectionHeading;
  const pageSummary = (input.pageSummary ?? [])
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length >= 12 && !isJunkSpokenHeading(s))
    .slice(0, 2)
    .join(" ");
  const includeMeta = input.includeMetaInstructions === true;
  const meta = ENGAGED_NAV_META_INSTRUCTIONS;
  // Shorter side-cue when section mode always needs an out.
  const sectionOut = includeMeta
    ? meta
    : "Pick another on the side, or ask me.";

  if (input.mode === "section") {
    // Only speak when we have real continuity — no spam on unrelated sections.
    if (!topic) return null;
    if (pill) {
      return `Still on ${topic}. ${pill}? ${sectionOut}`;
    }
    return `Still on ${topic}. ${sectionOut}`;
  }

  // ── nav (new page) ────────────────────────────────────────────────────
  // Page summary first (path-specific monologue), then thread + pill.
  const bits: string[] = [];
  if (pageSummary) {
    bits.push(pageSummary);
  }
  if (pill) {
    if (topic) {
      bits.push(`You asked about ${topic}. Want to continue with: ${pill}?`);
    } else if (pageSummary) {
      bits.push(`A good next question: ${pill}?`);
    } else {
      bits.push(`${pill}?`);
    }
  } else if (topic) {
    bits.push(
      `You asked about ${topic}. Pick a question, or ask me anything.`,
    );
  }
  if (includeMeta) {
    bits.push(meta);
  }
  if (bits.length === 0) {
    return includeMeta
      ? meta
      : "Pick a question, or ask me anything.";
  }
  return bits.join(" ");
}

/**
 * Natural nav host — presentation only.
 * Speaks the corpus pill only — never "On {page/section}:" (sounds robotic and
 * often wrong, e.g. "On my website: what does BlueHatAI do on my website?").
 * Optional continuity when conversation focus matches. Yes/tap/silence fine.
 */
export function buildNavHostOffer(input: {
  pageTopic?: string | null;
  sectionHeading?: string | null;
  pill: string;
  /** Optional "you asked about X" from conversation focus. */
  continuityTopic?: string | null;
  /** Kept for call-site compatibility; unused. */
  closeIndex?: number;
}): string {
  // pageTopic / sectionHeading intentionally unused for speech — pills already
  // carry the substance; framing the section name is noise.
  void input.pageTopic;
  void input.sectionHeading;
  const pill = spokenPill(input.pill);
  const continuity = (input.continuityTopic ?? "").replace(/\s+/g, " ").trim();
  if (!pill) {
    return "Ask me anything about this page when you're ready.";
  }
  if (continuity) {
    return `You asked about ${continuity}. ${pill}?`;
  }
  return `${pill}?`;
}

/**
 * Visitor wants the booking / demo CTA (header or presented form).
 * Not a freeform product question — client should execute the same CTA as the
 * Book a Demo button (not send another SB turn).
 *
 * Telemetry 6cc36402: "so book the demo" and STT "Boca demo" went to chat and
 * the model re-pitched the demo instead of launching the CTA.
 */
export function isBookDemoCommand(text: string): boolean {
  let t = text
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/[?.!]+$/g, "")
    .trim();
  if (!t || t.length > 80) return false;
  // Drop leading discourse: "so book the demo", "okay book a demo", "yes book demo"
  t = t
    .replace(
      /^(so|okay|ok|alright|all right|yes|yeah|yep|yup|please|then|and|well|um|uh)[,.]?\s+/i,
      "",
    )
    .trim();
  // Common STT mishear of "book a demo"
  if (/^boc+a\s+demo$/.test(t)) return true;
  // Bare commits once CTA is on the table (QA: "Book" / "Demo")
  if (/^(book|demo)$/.test(t)) return true;

  // Short intents — "book the demo", "book a demo", "book demo", …
  if (
    /^(book(\s+(a|the|my|me(\s+a)?))?\s+demo|schedule(\s+(a|the))?\s+demo|request(\s+(a|the))?\s+demo|get(\s+(a|the))?\s+demo|sign\s*up|demo\s+please|i\s+want(\s+(a|the|to\s+see(\s+(a|the))?))?\s+demo|let'?s\s+(book|do|see)(\s+(a|the))?\s+demo|show\s+me(\s+(a|the))?\s+demo|see(\s+(a|the))?\s+demo(\s+path)?)$/.test(
      t,
    )
  ) {
    return true;
  }
  // "book me a demo" / "I want to book the demo now" / "take me to the demo"
  if (
    /\b(book|schedule|request|open|start|launch|show|see)\b/.test(t) &&
    /\b(demo|sign[\s-]?up)\b/.test(t) &&
    // Avoid product questions: "how much does the demo cost"
    !/\b(how much|cost|price|pricing|what is|what'?s|where is)\b/.test(t)
  ) {
    return true;
  }
  return false;
}

/** Visitor accepts the offered nav pill. */
export function isNavYesCommand(text: string): boolean {
  const t = text
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/[?.!]+$/g, "")
    .trim();
  if (!t || t.length > 48) return false;
  if (
    /^(yes|yeah|yep|yup|sure|ok|okay|please|alright|all right)(\s+please)?$/.test(t)
  ) {
    return true;
  }
  if (
    /^(yes|yeah|yep|sure|ok|okay)[,.]?\s+(please|go ahead|answer|do it|tell me)/.test(
      t,
    )
  ) {
    return true;
  }
  return /^(go ahead|answer( that| it)?|do it|tell me|let'?s hear it|hit me)$/.test(
    t,
  );
}

/** Visitor declines the offer — stay quiet, don't open the panel. */
export function isNavNoCommand(text: string): boolean {
  const t = text
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/[?.!]+$/g, "")
    .trim();
  if (!t || t.length > 40) return false;
  return /^(no|nope|nah|not now|later|skip|pass|no thanks|no thank you)$/.test(t);
}

/**
 * Spoken list when the visitor asks the guide to read on-screen questions.
 * Still guardrailed: only live pills + fixed host lines.
 */
export function buildReadThemSpeech(input: VoiceBridgeInput): string {
  const now = (input.currentQuestions ?? []).slice(0, 3).map(spokenPill).filter(Boolean);
  const next = (input.nextQuestions ?? []).slice(0, 2).map(spokenPill).filter(Boolean);
  const here = titleish(input.currentHeading ?? "");

  if (now.length === 0 && next.length === 0) {
    return "I don’t have labeled questions on screen right now. Go ahead and ask anything about this page, or say stop talking.";
  }

  const parts: string[] = [];
  if (here) {
    parts.push(`Sure — for ${here}:`);
  } else {
    parts.push("Sure — here they are:");
  }

  const ordinal = (i: number) =>
    i === 0 ? "First" : i === 1 ? "Second" : i === 2 ? "Third" : "Also";

  now.forEach((q, i) => {
    parts.push(`${ordinal(i)}: ${q}?`);
  });

  if (next.length > 0) {
    parts.push(`And coming up: ${next.join(". Or: ")}.`);
  }

  parts.push(
    "Tap one so I can answer you, say it out loud, or ask something else. Say stop talking anytime.",
  );

  return parts.join(" ");
}

/**
 * Soft re-invite after an answer when no pills remain.
 */
export function buildVoiceContinueCue(): string {
  return "You can ask me anything else, or say stop talking.";
}

/**
 * Spoken “next steps” from live on-screen pills only.
 * Pure host copy — no LLM, no backend, does not change pills or Sales Brain.
 * So eyes-off visitors still hear what they can ask next.
 */
export function buildVoiceNextSteps(
  questions: readonly string[],
  opts?: { max?: number },
): string {
  const max = opts?.max ?? 3;
  const qs = questions
    .map((q) => spokenPill(q))
    .filter(Boolean)
    .slice(0, max);
  if (qs.length === 0) {
    return buildVoiceContinueCue();
  }
  if (qs.length === 1) {
    return `Next — you can also ask: ${qs[0]}? Or ask me anything. Say stop talking anytime.`;
  }
  if (qs.length === 2) {
    return `Next — you can also ask: ${qs[0]}? Or: ${qs[1]}? Or ask me anything. Say stop talking anytime.`;
  }
  return `Next — pick another question, like: ${qs[0]}? Or: ${qs[1]}? Or: ${qs[2]}? Or ask me anything. Say stop talking anytime.`;
}

/** Visitor asked the guide to read the on-screen questions (not a product Q). */
export function isReadThemCommand(text: string): boolean {
  const t = text.replace(/\s+/g, " ").trim().toLowerCase().replace(/[?.!]+$/, "");
  if (!t) return false;
  // Affirmative + read/list (e.g. "yes, read them for me").
  if (
    /^(yes|yeah|yep|sure|ok|okay|please|alright|all right)[,.]?\s+/.test(t) &&
    /\b(read|list)\b/.test(t) &&
    !/\b(what|how|why|when|where|price|cost|setup)\b/.test(t)
  ) {
    return true;
  }
  // Exact-ish guide intents only — avoid "what are they using for …".
  if (
    /^(read them|read it|read those|read the questions|read them for me|read them out|read them out loud|read them aloud|list them|list the questions|go ahead and read|go ahead and read them)$/.test(
      t,
    )
  ) {
    return true;
  }
  if (/^(what are they|what are the questions|what are those)$/.test(t)) {
    return true;
  }
  return /\bread (them|those|the questions)\b/.test(t) && t.length < 48;
}
